<template>
  <div class="markers-wrapper">
    <div ref="canvasWrapper" :style="bgStyle">
      <PolygonControls v-if="activeElementType === 'marker'" :scale="scale" />

      <div v-for="item in propertyMarkers" :key="item._id">
        <div
          :id="item._id"
          class="dot"
          :class="{
            'dot--selected': isActive(item._id),
            inactive: isInactive(item._id),
          }"
          :style="{
            left: item.marker.x * scale + 'px',
            top: item.marker.y * scale + 'px',
          }"
          @pointerdown="onMouseDown(item, $event)"
          @pointerup="onMouseUp($event)"
          @pointermove="onMouseDrag($event)"
          @dblclick="onDoubleClick(item, $event)"
        >
          <div class="dot__label">{{ item.label }}</div>
        </div>
      </div>

      <div
        v-for="item in [...subsectionMarkers, ...livingOptionMarkers]"
        :key="item._id"
      >
        <tag-basic
          v-bind="item.marker"
          :_id="item._id"
          :class="{
            active: isActive(item._id),
            inactive: isInactive(item._id),
          }"
          :pos="{
            x: item.marker.x,
            y: item.marker.y,
          }"
          :label="
            item.displayName[activeLanguage.code] ||
            item.displayName['en'] ||
            item.label
          "
          :style="`transform: rotate(${item.marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event)"
          @dblclick.native="onDoubleClick(item, $event)"
        />
      </div>

      <reference-tag-slider
        v-if="referenceTagsSlider.length > 0"
        :isVertical="true"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop="
          {
          }
        "
      />
    </div>
  </div>
</template>

<script>
import { orderBy } from "lodash";
import { mapState } from "vuex";
import { mapGetters } from "vuex";
import TagBasic from "@/components/TagBasic";
import PolygonControls from "@/components/PolygonControls";

export default {
  props: [
    "value",
    "selected-elements",
    "property-ids",
    "subsection-ids",
    "living-option-ids",
    "background-image",
    "zoom-level",
  ],

  components: {
    TagBasic,
    PolygonControls,
  },

  data() {
    return {
      scale: 1,
      draggableMarker: null,

      clickOffsetX: null,
      clickOffsetY: null,
    };
  },

  computed: {
    ...mapState("draggable", ["activeElement", "activeElementType"]),

    ...mapGetters([
      "activeLanguage",
      "page",
      "properties",
      "subsections",
      "livingOptions",
    ]),

    tags: {
      get() {
        return this.value;
      },
      set(tags) {
        this.$emit("input", tags);
      },
    },

    propertyMarkers() {
      if (this.propertyIds) {
        return this.properties.filter((property) =>
          this.propertyIds.includes(property._id)
        );
      }

      return [];
    },

    subsectionMarkers() {
      if (this.subsectionIds) {
        return this.subsections.filter((subsection) =>
          this.subsectionIds.includes(subsection._id)
        );
      }

      return [];
    },

    livingOptionMarkers() {
      if (this.livingOptionIds) {
        return this.livingOptions.filter(
          (option) => this.livingOptionIds.includes(option._id) && option.active
        );
      }

      return [];
    },

    bgStyle() {
      if (this.scale) {
        return {
          ["font-size"]: 20 * this.scale + "px",
          ["height"]: 1960 * this.scale + "px",
        };
      }
      return {};
    },

    referenceTagsSlider() {
      if (this.tags) {
        let refTags = this.tags.filter(function (tag) {
          return tag.type == "reference";
        });

        refTags = orderBy(refTags, ["pos.x"], ["asc"]);

        if (this.dir === "rtl") {
          let startingIndex = refTags.length;

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--;
            }
          });
          return refTags;
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1;
            }
          });
          return refTags;
        }
      } else {
        return [];
      }
    },
  },

  watch: {
    zoomLevel() {
      this.getScaledAmount();
    },

    propertyMarkers: {
      handler(markers) {
        (markers || []).forEach(this.ensureVisibleMarker);
      },
      immediate: true,
    },

    subsectionMarkers: {
      handler(markers) {
        (markers || []).forEach(this.ensureVisibleMarker);
      },
      immediate: true,
    },

    livingOptionMarkers: {
      handler(markers) {
        (markers || []).forEach(this.ensureVisibleMarker);
      },
      immediate: true,
    },
  },

  mounted() {
    window.addEventListener("resize", this.getScaledAmount);
    this.$nextTick(() => {
      this.getScaledAmount();
    });
  },

  methods: {
    getTagBoundingBox(tagEl, type) {
      // Some tags are structured differently and won't work properly
      // Basic icon, Reveal, Box
      const canvasHeight = 1960;
      const canvasWidth = 3190;
      const canvasLayer = document
        .querySelector(".draggable-container")
        .getBoundingClientRect();

      const elementClasses = [
        ".tag-dot",
        ".tag-rich",
        ".tag-rich__asset-col",
        ".tag-basic",
        ".tag-action",
        ".tag-ref",
      ];

      const selectorString = elementClasses.join(", ");
      const children = tagEl.querySelectorAll(selectorString);
      const elements = [tagEl, ...Array.from(children)];

      const tagAnchorRect = tagEl.getBoundingClientRect();

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      // Loop through all elements and update the min/max coordinates
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) {
          minX = Math.min(minX, rect.left);
          minY = Math.min(minY, rect.top);
          maxX = Math.max(maxX, rect.right);
          maxY = Math.max(maxY, rect.bottom);
        }
      });

      // tag distance from the canvas edge in pixels
      const leftDistance = minX - canvasLayer.left;
      const rightDistance = canvasLayer.right - maxX;
      const topDistance = minY - canvasLayer.top;
      const bottomDistance = canvasLayer.bottom - maxY;

      // anchor position relative to tag element
      const anchorLeft = tagAnchorRect.left - canvasLayer.left;
      const anchorRight = canvasLayer.right - tagAnchorRect.right;
      const anchorTop = tagAnchorRect.top - canvasLayer.top;
      const anchorBottom = canvasLayer.bottom - tagAnchorRect.bottom;

      // determine the bounds
      const anchorXMin = Math.round((anchorLeft - leftDistance) / this.scale);
      const anchorXMax =
        canvasWidth - Math.round((anchorRight - rightDistance) / this.scale);
      const anchorYMin = Math.round((anchorTop - topDistance) / this.scale);
      const anchorYMax =
        canvasHeight - Math.round((anchorBottom - bottomDistance) / this.scale);

      return {
        xMin: anchorXMin,
        xMax: anchorXMax,
        yMin: anchorYMin,
        yMax: anchorYMax,
        x: tagAnchorRect.left,
        y: tagAnchorRect.top,
      };
    },

    // initial position of new marker
    ensureVisibleMarker(item) {
      if (!item || !item.marker) return;

      const m = item.marker;
      const isUnplaced =
        (m.x === 0 || m.x == null) && (m.y === 0 || m.y == null);

      if (isUnplaced) {
        const { x, y } = this.clampToCanvas(200, 100);
        this.$set(m, "x", x);
        this.$set(m, "y", y);
      }
    },

    // shared clamping helper
    clampToCanvas(x, y) {
      if (!this.$refs.canvasWrapper || !this.scale) {
        return { x, y };
      }

      const canvasWidth = this.$refs.canvasWrapper.offsetWidth;
      const canvasHeight = this.$refs.canvasWrapper.offsetHeight;

      const maxX = canvasWidth / this.scale;
      const maxY = canvasHeight / this.scale;

      const clampedX = Math.max(0, Math.min(x, maxX));
      const clampedY = Math.max(0, Math.min(y, maxY));

      return {
        x: Math.round(clampedX),
        y: Math.round(clampedY),
      };
    },

    getScaledAmount() {
      // default size based on 3190x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth =
          this.$refs.canvasWrapper.getBoundingClientRect().width;
        let scaled = canvasWidth / 3190;
        this.scale = scaled;
      }
    },

    onMouseDrag(event) {
      if (!this.draggableMarker) return;

      const tagEl = document.getElementById(this.activeElement._id);

      console.log("tagEl", tagEl);

      const tagBounds = this.getTagBoundingBox(tagEl, this.activeElement.type);

      console.log("tagBounds", tagBounds);

      const canvasEl = document.querySelector(".draggable-container__bg");
      const canvasRect = canvasEl.getBoundingClientRect();

      if (!this.clickOffsetX) {
        this.clickOffsetX = tagBounds.x - event.pageX;
      }
      if (!this.clickOffsetY) {
        this.clickOffsetY = tagBounds.y - event.pageY;
      }

      const layerX =
        (event.clientX - canvasRect.left + this.clickOffsetX) / this.scale;
      const layerY =
        (event.clientY - canvasRect.top + this.clickOffsetY) / this.scale;

      const cx = Math.min(Math.max(layerX, tagBounds.xMin), tagBounds.xMax);
      const cy = Math.min(Math.max(layerY, tagBounds.yMin), tagBounds.yMax);

      this.draggableMarker.x = cx;
      this.draggableMarker.y = cy;
    },

    addTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then((res) => {
        this.tags.push(res.data);
      });
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit("openAssetLibrary", context, assetSelected);
    },

    onMouseDown(item, e) {
      e.target.setPointerCapture(e.pointerId);

      this.draggableMarker = item.marker;

      this.$store.commit("draggable/addToSelectedElements", {
        element: item,
        elementType: "marker",
        isSingle: !e.shiftKey,
      });

      this.$emit("record-before-action", item);
    },

    onMouseUp(e) {
      e.target.releasePointerCapture(e.pointerId);
      this.draggableMarker = null;
      this.clickOffsetX = null;
      this.clickOffsetY = null;
    },

    updateActiveDotPosition(e) {
      if (this.draggableMarker) {
        this.draggableMarker.pos.x = Math.round(
          this.draggableMarker.pos.x + e.movementX / this.scale
        );
        this.draggableMarker.pos.y = Math.round(
          this.draggableMarker.pos.y + e.movementY / this.scale
        );
      }
    },

    isActive(id) {
      const item = this.selectedElements.find((element) => element._id === id);
      return !_.isEmpty(item);
    },

    isInactive(id) {
      if (!this.selectedElements.length) return false;

      const item = this.selectedElements.find((element) => element._id === id);
      return _.isEmpty(item);
    },

    updatePolygon(targetItem, polygon) {
      targetItem.polygon = polygon;
    },

    openMenu() {
      this.$store.commit("draggable/setIsMenuOpen", true);
    },

    onDoubleClick(item, event) {
      // shift+double click to select all markers
      if (event && event.shiftKey) {
        this.$emit("select-all-markers");
        return;
      }

      // always make this marker the single active selection on dblclick
      this.$store.commit("draggable/addToSelectedElements", {
        element: item,
        elementType: "marker",
        isSingle: true,
      });

      // property markers should NEVER open the edit menu
      const isPropertyMarker =
        Array.isArray(this.propertyIds) && this.propertyIds.includes(item._id);

      if (isPropertyMarker) {
        this.$store.commit("draggable/setIsMenuOpen", false); // optional safety
        return;
      }

      // if somehow multi-selected, don't open
      if (this.selectedElements && this.selectedElements.length > 1) {
        return;
      }

      this.$store.commit("draggable/setIsMenuOpen", true);
    },
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getScaledAmount);
  },
};
</script>

<style lang="scss" scoped>
.markers-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.dot {
  border-radius: 50%;
  background-color: red;
  cursor: pointer;
  pointer-events: all;
  position: absolute;
  font-size: 0.75em;
  height: 3em;
  width: 3em;
  user-select: none;
  opacity: 1;
  transform: translate(-0.25em, -0.4em);
  z-index: 6;

  &__label {
    background: #fff;
    bottom: 3.5em;
    left: 50%;
    opacity: 0;
    padding: 0.5em;
    pointer-events: none;
    position: absolute;
    transform: translateX(-50%);
    width: max-content;
  }

  &:hover {
    .dot__label {
      opacity: 1;
    }
  }

  &--selected {
    border: 2px solid #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.4);
  }
}

.inactive {
  opacity: 0.5;
}
</style>
